import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';
import {
  Lock,
  ArrowRight,
  Check,
  Target,
  Cog,
  BarChart3,
  Layers,
  Users,
  Sparkles,
  TrendingUp,
  Minus,
  Award,
  AlertTriangle,
  ChevronRight,
  Info,
  Share2,
  Copy,
  Mail,
  CheckCircle
} from 'lucide-react';
import useAssessmentStore from '../hooks/useAssessment';
import { getTierColor } from '../lib/scoring';
import { 
  INDUSTRY_BENCHMARKS, 
  calculatePercentile, 
  calculateCostOfInefficiency,
  getDimensionComparison,
  RESEARCH_CITATIONS
} from '../lib/benchmarks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CAL_LINK = 'https://cal.com/sid-das/unpaved-demo?overlayCalendar=true';

const ResultsDashboard = () => {
  const navigate = useNavigate();
  const { userData, answers, results } = useAssessmentStore();
  const [animatedScore, setAnimatedScore] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [resultId, setResultId] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const scoreRef = useRef(null);

  const industry = userData?.industry || 'other';
  const companySize = userData?.companySize || '201-500';
  const benchmark = INDUSTRY_BENCHMARKS[industry] || INDUSTRY_BENCHMARKS.other;

  // Redirect if no results
  useEffect(() => {
    if (!results || !userData) {
      navigate('/');
    }
  }, [results, userData, navigate]);

  // Animate score counter with confetti
  useEffect(() => {
    if (!results) return;

    const duration = 2500;
    const startTime = Date.now();
    const targetScore = results.overallScore;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Cubic ease-out with bounce
      const eased = 1 - Math.pow(1 - progress, 4);
      const current = Math.round(eased * targetScore);
      
      setAnimatedScore(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Trigger confetti for good scores
        if (targetScore >= 60) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }
      }
    };

    requestAnimationFrame(animate);
  }, [results]);

  // Submit assessment data to backend
  useEffect(() => {
    if (!results || !userData || hasSubmitted) return;

    const submitAssessment = async () => {
      try {
        const payload = {
          user: userData,
          answers,
          scores: {
            overall: results.overallScore,
            tier: results.tier,
            dimensions: results.dimensionScores.map(d => ({
              dimensionId: d.dimensionId,
              rawScore: d.rawScore,
              maxPossible: d.maxPossible,
              percentage: d.percentage,
              tier: d.tier,
              color: d.color
            }))
          },
          completedAt: new Date().toISOString()
        };

        const response = await fetch(`${BACKEND_URL}/api/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        const data = await response.json();
        if (data.id) {
          setResultId(data.id);
        }

        setHasSubmitted(true);
      } catch (error) {
        console.error('Failed to submit assessment:', error);
      }
    };

    submitAssessment();
  }, [results, userData, answers, hasSubmitted]);

  // Share functions
  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/results/${resultId}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleEmailShare = () => {
    if (!shareEmail) return;
    const subject = encodeURIComponent(`${userData?.company}'s AI Org Health Score Results`);
    const body = encodeURIComponent(
      `Hi,\n\nI wanted to share our AI Org Health Score results with you.\n\n` +
      `Overall Score: ${results.overallScore}/100 (${results.tier})\n\n` +
      `View full results: ${getShareUrl()}\n\n` +
      `Take your own assessment at ${window.location.origin}`
    );
    window.open(`mailto:${shareEmail}?subject=${subject}&body=${body}`);
    setShowShareModal(false);
    setShareEmail('');
  };

  // Get icon component for dimension
  const getIcon = (iconName) => {
    const icons = { Target, Cog, BarChart3, Layers, Users, Sparkles };
    const Icon = icons[iconName];
    return Icon ? <Icon className="w-5 h-5" strokeWidth={1.5} /> : null;
  };

  // Get position indicator
  const getPositionIndicator = (position) => {
    switch (position) {
      case 'top_performer':
        return { icon: Award, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Top Performer' };
      case 'above_average':
        return { icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Above Average' };
      case 'below_average':
        return { icon: Minus, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Below Average' };
      case 'needs_attention':
        return { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', label: 'Needs Attention' };
      default:
        return { icon: Minus, color: 'text-zinc-600', bg: 'bg-zinc-50', label: 'Average' };
    }
  };

  const getBarColor = (color) => {
    switch (color) {
      case 'red': return '#ef4444';
      case 'amber': return '#f59e0b';
      case 'green': return '#10b981';
      default: return '#71717a';
    }
  };

  if (!results) return null;

  const percentile = calculatePercentile(results.overallScore, industry);
  const costOfInefficiency = calculateCostOfInefficiency(results.overallScore, industry, companySize);

  // Prepare radar chart data with industry comparison
  const radarData = results.dimensionScores.map(d => {
    const dimBenchmark = benchmark.dimensions[d.dimensionId];
    return {
      dimension: d.name.split(' ')[0],
      fullName: d.name,
      score: d.percentage,
      industryAvg: dimBenchmark?.avg || 50,
      industryTop: dimBenchmark?.top || 75,
      fullMark: 100
    };
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-zinc-200 shadow-lg p-4 max-w-xs">
          <p className="font-semibold text-zinc-900 mb-2">{data.fullName}</p>
          <div className="space-y-1 text-sm">
            <p className="flex justify-between">
              <span className="text-zinc-500">Your Score:</span>
              <span className="font-semibold text-zinc-900">{data.score}%</span>
            </p>
            <p className="flex justify-between">
              <span className="text-zinc-500">Industry Avg:</span>
              <span className="text-zinc-700">{data.industryAvg}%</span>
            </p>
            <p className="flex justify-between">
              <span className="text-zinc-500">Top Quartile:</span>
              <span className="text-emerald-600">{data.industryTop}%</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Confetti Effect */}
      <AnimatePresence>
        {showConfetti && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 pointer-events-none z-50 overflow-hidden"
          >
            {[...Array(50)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ 
                  y: -20, 
                  x: Math.random() * window.innerWidth,
                  rotate: 0,
                  opacity: 1
                }}
                animate={{ 
                  y: window.innerHeight + 100,
                  rotate: Math.random() * 720 - 360,
                  opacity: 0
                }}
                transition={{ 
                  duration: 2 + Math.random() * 2,
                  delay: Math.random() * 0.5,
                  ease: "linear"
                }}
                className="absolute w-3 h-3"
                style={{
                  backgroundColor: ['#6c5ce7', '#00d2a0', '#f7b731', '#fd79a8', '#00cec9'][Math.floor(Math.random() * 5)]
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-6 border-b border-zinc-100">
        <div className="flex items-center justify-between">
          <a 
            href="https://unpaved.ai" 
            target="_blank" 
            rel="noopener noreferrer"
            className="font-serif text-xl tracking-tight hover:opacity-70 transition-opacity"
          >
            Unpaved
          </a>
          <div className="flex items-center gap-4">
            {resultId && (
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-1.5 hover:border-zinc-400 transition-colors"
                data-testid="share-results-btn"
              >
                <Share2 className="w-4 h-4" />
                Share Results
              </button>
            )}
            <span className="text-xs font-medium tracking-wide uppercase text-zinc-400 border border-zinc-200 px-3 py-1.5">
              {benchmark.name}
            </span>
          </div>
        </div>
      </header>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white p-8 max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-serif text-2xl mb-2">Share Your Results</h3>
              <p className="text-sm text-zinc-600 mb-6">
                Share your AI Org Health Score with colleagues or stakeholders.
              </p>

              {/* Copy Link */}
              <div className="mb-6">
                <label className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">
                  Share Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={getShareUrl()}
                    className="flex-1 border border-zinc-200 px-3 py-2 text-sm bg-zinc-50 text-zinc-600"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 transition-colors"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Email Share */}
              <div className="mb-6">
                <label className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">
                  Share via Email
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="colleague@company.com"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    className="flex-1 border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-400 focus:outline-none"
                  />
                  <button
                    onClick={handleEmailShare}
                    disabled={!shareEmail}
                    className="flex items-center gap-2 border-2 border-zinc-950 text-zinc-950 px-4 py-2 text-sm font-medium hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Mail className="w-4 h-4" />
                    Send
                  </button>
                </div>
              </div>

              <button
                onClick={() => setShowShareModal(false)}
                className="w-full text-center text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-6xl mx-auto"
        >
          {/* Score Hero Section */}
          <motion.div
            variants={itemVariants}
            className="text-center mb-12"
          >
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-4">
              Your AI Org Health Score
            </p>
            
            <div className="mb-4" ref={scoreRef}>
              <motion.span 
                className="font-serif text-8xl sm:text-9xl tracking-tight inline-block"
                style={{ color: results.tierColor }}
                data-testid="overall-score"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                {animatedScore}
              </motion.span>
              <span className="text-4xl text-zinc-300 font-serif">/100</span>
            </div>

            {/* Tier Badge */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className="inline-flex items-center px-5 py-2.5 text-sm font-semibold mb-6"
              style={{
                backgroundColor: `${results.tierColor}15`,
                color: results.tierColor,
                border: `2px solid ${results.tierColor}40`
              }}
              data-testid="tier-badge"
            >
              {results.tier}
            </motion.div>

            {/* Percentile Badge */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="mb-8"
            >
              <div className="inline-flex items-center gap-2 bg-zinc-50 border border-zinc-200 px-4 py-2">
                <TrendingUp className="w-4 h-4 text-zinc-600" />
                <span className="text-sm text-zinc-700">
                  You're in the <span className="font-semibold text-zinc-900">top {Math.round(100 - percentile)}%</span> of {benchmark.name} organisations
                </span>
              </div>
            </motion.div>

            {/* Tier Summary */}
            <p className="text-lg text-zinc-600 leading-relaxed max-w-2xl mx-auto">
              {results.summary}
            </p>
          </motion.div>

          {/* Key Metrics Row */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
          >
            {/* Percentile Card */}
            <div className="border border-zinc-200 p-6 text-center">
              <div className="text-4xl font-serif text-zinc-900 mb-2">
                Top {Math.round(100 - percentile)}%
              </div>
              <p className="text-sm text-zinc-500">Industry Percentile</p>
              <p className="text-xs text-zinc-400 mt-2">vs. {benchmark.name}</p>
            </div>

            {/* Cost of Inefficiency Card */}
            <div className="border border-zinc-200 p-6 text-center bg-red-50/30">
              <div className="text-4xl font-serif text-red-600 mb-2">
                ${costOfInefficiency.low.toLocaleString()}–{costOfInefficiency.high.toLocaleString()}
              </div>
              <p className="text-sm text-zinc-500">Est. Annual Cost of Gaps</p>
              <p className="text-xs text-zinc-400 mt-2">Based on industry benchmarks</p>
            </div>

            {/* Improvement Potential Card */}
            <div className="border border-zinc-200 p-6 text-center bg-emerald-50/30">
              <div className="text-4xl font-serif text-emerald-600 mb-2">
                +{Math.max(0, benchmark.topQuartile - results.overallScore)}pts
              </div>
              <p className="text-sm text-zinc-500">Potential Improvement</p>
              <p className="text-xs text-zinc-400 mt-2">To reach top quartile</p>
            </div>
          </motion.div>

          {/* Two Column Layout: Radar + Dimensions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Enhanced Radar Chart */}
            <motion.div
              variants={itemVariants}
              className="border border-zinc-200 p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-2xl tracking-tight">
                  Dimension Breakdown
                </h3>
                <button
                  onClick={() => setActiveTooltip(activeTooltip === 'radar' ? null : 'radar')}
                  className="text-zinc-400 hover:text-zinc-600 transition-colors"
                >
                  <Info className="w-5 h-5" />
                </button>
              </div>

              {activeTooltip === 'radar' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-4 bg-zinc-50 border border-zinc-200 text-sm text-zinc-600"
                >
                  <p className="mb-2"><strong>How to read this chart:</strong></p>
                  <ul className="space-y-1 text-xs">
                    <li>• <span className="text-zinc-900 font-medium">Black line</span>: Your scores across 6 dimensions</li>
                    <li>• <span className="text-blue-600 font-medium">Blue dashed</span>: Industry average ({benchmark.name})</li>
                    <li>• Further from center = stronger performance</li>
                  </ul>
                </motion.div>
              )}

              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                    <PolarGrid stroke="#E4E4E7" />
                    <PolarAngleAxis
                      dataKey="dimension"
                      tick={{ fill: '#52525B', fontSize: 11, fontWeight: 500 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    
                    {/* Industry Average Line */}
                    <Radar
                      name="Industry Average"
                      dataKey="industryAvg"
                      stroke="#3b82f6"
                      fill="transparent"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      isAnimationActive={true}
                      animationDuration={1000}
                    />
                    
                    {/* Your Score */}
                    <Radar
                      name="Your Score"
                      dataKey="score"
                      stroke="#09090B"
                      fill="#09090B"
                      fillOpacity={0.15}
                      strokeWidth={2.5}
                      isAnimationActive={true}
                      animationDuration={1200}
                      animationBegin={200}
                    />
                    
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      formatter={(value) => (
                        <span className="text-sm text-zinc-600">{value}</span>
                      )}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              {/* Industry Insight */}
              <div className="mt-6 p-4 bg-zinc-50 border-l-4 border-zinc-900">
                <p className="text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">
                  {benchmark.name} Insight
                </p>
                <p className="text-sm text-zinc-700">
                  <strong>Typical strength:</strong> {benchmark.insights.strength}
                </p>
                <p className="text-sm text-zinc-700 mt-1">
                  <strong>Common gap:</strong> {benchmark.insights.weakness}
                </p>
              </div>
            </motion.div>

            {/* Enhanced Dimension Cards */}
            <motion.div variants={itemVariants} className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-serif text-2xl tracking-tight">By Dimension</h3>
                <div className="flex items-center gap-4 text-xs text-zinc-500">
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-zinc-900 rounded-full"></div>
                    Your Score
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    Industry Avg
                  </span>
                </div>
              </div>

              {results.dimensionScores.map((dimension, index) => {
                const comparison = getDimensionComparison(dimension.dimensionId, dimension.percentage, industry);
                const positionInfo = getPositionIndicator(comparison?.position);
                const PositionIcon = positionInfo.icon;

                return (
                  <motion.div
                    key={dimension.dimensionId}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.08 }}
                    className="border border-zinc-200 p-5 hover:border-zinc-400 transition-colors"
                    data-testid={`dimension-card-${dimension.dimensionId}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="p-2 rounded"
                          style={{ backgroundColor: `${getBarColor(dimension.color)}15` }}
                        >
                          {getIcon(dimension.icon)}
                        </div>
                        <div>
                          <span className="font-medium text-zinc-900">{dimension.name}</span>
                          <div className={`inline-flex items-center gap-1 ml-2 px-2 py-0.5 text-xs font-medium ${positionInfo.bg} ${positionInfo.color}`}>
                            <PositionIcon className="w-3 h-3" />
                            {positionInfo.label}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span
                          className="text-2xl font-semibold"
                          style={{ color: getBarColor(dimension.color) }}
                        >
                          {dimension.percentage}%
                        </span>
                        {comparison && (
                          <div className="text-xs text-zinc-500">
                            {comparison.vsAvg >= 0 ? '+' : ''}{comparison.vsAvg} vs avg
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Dual Progress Bar */}
                    <div className="relative h-3 bg-zinc-100 mb-3">
                      {/* Industry Average Marker */}
                      {comparison && (
                        <div 
                          className="absolute top-0 bottom-0 w-0.5 bg-blue-500 z-10"
                          style={{ left: `${comparison.industryAvg}%` }}
                        >
                          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-blue-500 rounded-full"></div>
                        </div>
                      )}
                      {/* Your Score Bar */}
                      <motion.div
                        className="absolute top-0 bottom-0 left-0"
                        style={{ backgroundColor: getBarColor(dimension.color) }}
                        initial={{ width: 0 }}
                        animate={{ width: `${dimension.percentage}%` }}
                        transition={{ duration: 1, delay: 0.6 + index * 0.08 }}
                      />
                    </div>
                    
                    <p className="text-sm text-zinc-500">{dimension.feedback}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

          {/* Research-Backed Insight Section */}
          <motion.div
            variants={itemVariants}
            className="border border-zinc-200 p-8 mb-12"
          >
            <h3 className="font-serif text-2xl tracking-tight mb-6">
              Research-Backed Context
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(RESEARCH_CITATIONS).slice(0, 3).map(([key, citation]) => (
                <div key={key} className="p-4 bg-zinc-50 border-l-4 border-zinc-300">
                  <p className="text-sm text-zinc-700 mb-2">"{citation.stat}"</p>
                  <p className="text-xs text-zinc-400">— {citation.source}, {citation.year}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Locked Insights Section */}
          <motion.div
            variants={itemVariants}
            className="relative border border-zinc-200 p-8 mb-12 overflow-hidden"
            data-testid="locked-insights-section"
          >
            <div className="relative">
              <h3 className="font-serif text-2xl tracking-tight mb-6">
                Your Full Diagnostic Report Includes...
              </h3>
              
              {/* Blurred Content */}
              <div className="space-y-4 locked-blur select-none">
                <div className="flex items-start gap-3 p-4 bg-zinc-50">
                  <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-zinc-900">Detailed Cost Analysis</p>
                    <p className="text-sm text-zinc-600">Estimated annual cost: ${costOfInefficiency.low.toLocaleString()} - ${costOfInefficiency.high.toLocaleString()} in operational inefficiencies</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-zinc-50">
                  <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-zinc-900">Priority Automation Candidates</p>
                    <p className="text-sm text-zinc-600">3 processes identified for immediate AI deployment with 90-day ROI projection</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-zinc-50">
                  <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-zinc-900">90-Day Transformation Roadmap</p>
                    <p className="text-sm text-zinc-600">Phased implementation plan with quick wins, milestones, and success metrics</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-zinc-50">
                  <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-zinc-900">AI Readiness Deep-Dive</p>
                    <p className="text-sm text-zinc-600">Function-by-function analysis with specific tool recommendations</p>
                  </div>
                </div>
              </div>

              {/* Lock Overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
                <div className="bg-white border border-zinc-200 shadow-lg p-6 text-center max-w-sm">
                  <Lock className="w-10 h-10 text-zinc-400 mx-auto mb-4" strokeWidth={1.5} />
                  <p className="font-semibold text-zinc-900 mb-2">Full Report Locked</p>
                  <p className="text-sm text-zinc-600 mb-4">
                    Book your free diagnostic deep-dive to unlock personalised insights and actionable recommendations.
                  </p>
                  <a
                    href={CAL_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-zinc-950 text-white px-6 py-3 text-sm font-medium hover:bg-zinc-800 transition-colors"
                  >
                    Unlock Full Report
                    <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Booking CTA Section */}
          <motion.div
            variants={itemVariants}
            className="border-2 border-zinc-950 p-8 md:p-12"
            data-testid="booking-cta-section"
          >
            <div className="max-w-3xl mx-auto text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1.2, type: "spring", stiffness: 200 }}
                className="inline-flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 mb-6 text-sm font-medium"
              >
                <AlertTriangle className="w-4 h-4" />
                You're leaving ${costOfInefficiency.midpoint.toLocaleString()}+ on the table annually
              </motion.div>

              <h3 className="font-serif text-3xl sm:text-4xl tracking-tight mb-4">
                See What Your Score Is Costing You
              </h3>
              
              <p className="text-zinc-600 leading-relaxed mb-8">
                Book a free 30-minute diagnostic deep-dive. We'll quantify the gaps specific to {benchmark.name}, 
                show you exactly where the money is bleeding, and map your 90-day AI transformation roadmap.
              </p>

              {/* Feature Checklist */}
              <div className="flex flex-wrap justify-center gap-6 mb-10">
                {[
                  'Specific cost-per-gap analysis',
                  'Prioritised 90-day roadmap',
                  `${benchmark.name} benchmarking`
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Check className="w-3 h-3 text-emerald-600" strokeWidth={2.5} />
                    </div>
                    <span className="text-sm text-zinc-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <motion.a
                href={CAL_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-diagnostic-btn"
                className="inline-flex items-center gap-3 bg-zinc-950 text-white px-12 py-5 text-lg font-medium hover:bg-zinc-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Book Your Deep-Dive
                <ArrowRight className="w-5 h-5" />
              </motion.a>

              <p className="text-xs text-zinc-400 mt-4">
                Free 30-minute session • No obligation • Typically books 2-3 days out
              </p>
            </div>
          </motion.div>

          {/* Methodology Note */}
          <motion.div
            variants={itemVariants}
            className="mt-12 p-6 bg-zinc-50 border border-zinc-200"
          >
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-zinc-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-zinc-600">
                <p className="font-medium text-zinc-900 mb-2">About This Assessment</p>
                <p className="mb-2">
                  This diagnostic is built on established operational maturity frameworks used in enterprise consulting. 
                  The 20 questions assess six core dimensions that research consistently identifies as critical to operational excellence.
                </p>
                <p className="mb-2">
                  <strong>Benchmarks:</strong> Industry comparisons are derived from aggregated patterns in operational assessments 
                  and publicly available research on digital maturity. Figures are indicative and designed to provide directional guidance.
                </p>
                <p className="text-zinc-500">
                  <strong>Cost estimates:</strong> Based on typical consulting engagement findings and industry-standard efficiency multipliers. 
                  These are illustrative projections — actual impact varies by organizational context. 
                  For precise analysis, we recommend a detailed diagnostic consultation.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Footer */}
          <motion.footer
            variants={itemVariants}
            className="text-center py-12 mt-12 border-t border-zinc-100"
          >
            <p className="text-sm text-zinc-400">
              Powered by <a href="https://unpaved.ai" target="_blank" rel="noopener noreferrer" className="font-serif hover:text-zinc-600 transition-colors">Unpaved AI</a> — We help organisations unlock operational intelligence through AI.
            </p>
          </motion.footer>
        </motion.div>
      </main>
    </div>
  );
};

export default ResultsDashboard;
